"use client";

import { useEffect, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import VideoFeed from "./VideoFeed";
import { TVideo } from "@/actions/types";
import Logo from "@/components/customs/Logo";
import { SupportedLanguage } from "@/lib/shared";
import lang from "@/app/[locale]/(main)/lang.json";
import { cn } from "@/lib/utils";

type TabType = "Personal" | "All" | "Job";

type VideoFeedWithTabsProps = {
  initialTab: TabType;
  initialVideo: TVideo;
  isLoggedIn: boolean;
  myId?: string;
  locale?: SupportedLanguage;
};

export default function VideoFeedWithTabs({
  initialTab,
  initialVideo,
  isLoggedIn,
  myId,
  locale = "en",
}: VideoFeedWithTabsProps) {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [isPending, startTransition] = useTransition();
  /** موبايل: إخفاء شريط التبويب بعد الانتقال لريلّين للأمام (index ≥ 2) */
  const [reelSlideIndex, setReelSlideIndex] = useState(0);
  const tabsHiddenMobile = reelSlideIndex >= 2;

  useEffect(() => {
    setReelSlideIndex(0);
  }, [initialTab]);

  const handleTabClick = (tab: TabType) => {
    // Immediate visual feedback
    setActiveTab(tab);

    // Navigate with transition for data fetching
    startTransition(() => {
      router.push(`?filter=${tab}`);
    });
  };

  return (
    <>
      <div
        className={cn(
          "pointer-events-none absolute left-0 right-0 top-4 z-30 h-16 rounded-t-2xl bg-gradient-to-b from-black/75 to-transparent transition-opacity duration-300 max-lg:top-0 max-lg:rounded-none max-lg:duration-300",
          tabsHiddenMobile && "max-lg:opacity-0 max-lg:pointer-events-none",
        )}
      />

      <div
        className={cn(
          "absolute left-1/2 top-4 z-40 flex h-16 -translate-x-1/2 items-center justify-center rounded-t-2xl text-gray-400 transition-opacity duration-300 max-lg:top-0 max-lg:rounded-none max-lg:duration-300",
          tabsHiddenMobile && "max-lg:pointer-events-none max-lg:opacity-0",
        )}
      >
        <div className="flex border-b-2 border-inherit text-sm font-bold max-lg:border-b-0">
          <button
            type="button"
            onClick={() => handleTabClick("Personal")}
            data-state={activeTab === "Personal" ? "active" : "inactive"}
            className={cn(
              "relative cursor-pointer px-4 py-2 transition-colors lg:text-inherit lg:data-[state=active]:text-white",
              "max-lg:text-white/80 max-lg:data-[state=active]:text-[#ffffff]",
            )}
            disabled={isPending}
          >
            {activeTab === "Personal" && (
              <span className="absolute bottom-0 left-0 right-0 hidden h-[6px] w-full translate-y-[4px] rounded-full bg-white lg:block" />
            )}
            {lang["tab-profiles"][locale]}
          </button>
          <button
            type="button"
            onClick={() => handleTabClick("All")}
            data-state={activeTab === "All" ? "active" : "inactive"}
            className={cn(
              "relative cursor-pointer px-4 py-2 transition-colors lg:text-inherit lg:data-[state=active]:text-white",
              "max-lg:text-white/80 max-lg:data-[state=active]:text-[#ffffff]",
            )}
            disabled={isPending}
          >
            {activeTab === "All" && (
              <span className="absolute bottom-0 left-0 right-0 hidden h-[6px] w-full translate-y-[4px] rounded-full bg-white lg:block" />
            )}
            {lang["tab-all"][locale]}
          </button>
          <button
            type="button"
            onClick={() => handleTabClick("Job")}
            data-state={activeTab === "Job" ? "active" : "inactive"}
            className={cn(
              "relative cursor-pointer px-4 py-2 transition-colors lg:text-inherit lg:data-[state=active]:text-white",
              "max-lg:text-white/80 max-lg:data-[state=active]:text-[#ffffff]",
            )}
            disabled={isPending}
          >
            {activeTab === "Job" && (
              <span className="absolute bottom-0 left-0 right-0 hidden h-[6px] w-full translate-y-[4px] rounded-full bg-white lg:block" />
            )}
            {lang["tab-jobs"][locale]}
          </button>
        </div>
      </div>

      {isPending && (
        <div className="absolute inset-0 z-20 bg-black/20 max-lg:rounded-none lg:left-0 lg:right-0 lg:top-4 lg:bottom-4 lg:rounded-2xl">
          <div className="absolute top-56 left-1/2 -translate-x-1/2 z-50">
            <div className="relative flex items-center justify-center w-24 h-24">
              <div className="text-white inline-flex mt-1">
                <Logo />
              </div>
              <div className="absolute inset-0 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
            </div>
          </div>
        </div>
      )}

      <VideoFeed
        locale={locale}
        key={initialTab}
        activeTab={initialTab}
        initialVideo={initialVideo}
        isLoggedIn={isLoggedIn}
        myId={myId}
        onSlideChange={setReelSlideIndex}
      />
    </>
  );
}
